/*
 * Shows automatic discounts on the custom Estee product cards (featured collection + collection pages)
 * and on the product page price (snippets/product-information-blocks.liquid, .estee-pdp-price).
 *
 * Shopify only calculates automatic discounts for items in a cart, so the cards price their
 * variants in a throwaway Storefront API cart (tokenless, never the shopper's own cart) and
 * show the discounted total the same way the cart drawer does.
 *
 * The throwaway cart copies the shopper's cart attributes and country, so app discounts that
 * depend on them (e.g. Intelligems offers, which read the visitor's test group from cart
 * attributes) apply exactly as they would in the real cart.
 */
(function () {
  if (window.esteeDiscountPrices) return;

  var API_URL = '/api/2026-07/graphql.json';
  var CACHE_KEY = 'estee-discount-prices';
  var CACHE_TTL = 5 * 60 * 1000;
  var APP_WAIT_MS = 5000; // max wait for Intelligems to assign the visitor's test group

  var CART_QUERY =
    'mutation EsteeCardPrices($lines: [CartLineInput!]!, $attributes: [AttributeInput!], $country: CountryCode)' +
    ' @inContext(country: $country) {' +
    '  cartCreate(input: { lines: $lines, attributes: $attributes }) {' +
    '    cart { lines(first: 100) { nodes {' +
    '      merchandise { ... on ProductVariant { id } }' +
    '      cost { subtotalAmount { amount } totalAmount { amount } }' +
    '    } } }' +
    '  }' +
    '}';

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function hasIntelligems() {
    return !!document.querySelector('script[src*="intelligems"]') || window.igLoaded !== undefined;
  }

  function hasIntelligemsAttributes(attributes) {
    return Object.keys(attributes).some(function (key) {
      return /^_?ig/i.test(key);
    });
  }

  function fetchCartAttributes() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return (cart && cart.attributes) || {};
      })
      .catch(function () {
        return {};
      });
  }

  // Cart attributes + country the throwaway cart should be priced with
  var contextPromise = null;
  function getContext() {
    if (contextPromise) return contextPromise;

    var waitForApps = Promise.resolve();
    if (hasIntelligems()) {
      var started = Date.now();
      waitForApps = (function poll() {
        if (window.igFinished || Date.now() - started > APP_WAIT_MS) return Promise.resolve();
        return wait(100).then(poll);
      })();
    }

    contextPromise = waitForApps
      .then(fetchCartAttributes)
      .then(function (attributes) {
        // Intelligems writes its attributes to the cart just after it finishes; give it one more moment
        if (hasIntelligems() && !hasIntelligemsAttributes(attributes)) {
          return wait(1000).then(fetchCartAttributes);
        }
        return attributes;
      })
      .then(function (attributes) {
        var list = Object.keys(attributes)
          .filter(function (key) {
            return attributes[key] !== null && attributes[key] !== '';
          })
          .sort()
          .map(function (key) {
            return { key: key, value: String(attributes[key]) };
          });
        var country = (window.Shopify && Shopify.country) || null;
        return { attributes: list, country: country, key: JSON.stringify([country, list]) };
      });
    return contextPromise;
  }

  function readCache(contextKey) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (cached && cached.context === contextKey && Date.now() - cached.time < CACHE_TTL) return cached.prices;
    } catch (e) {}
    return {};
  }

  function writeCache(contextKey, prices) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), context: contextKey, prices: prices }));
    } catch (e) {}
  }

  // variantId -> discounted price in cents, for one unit
  function fetchPrices(variantIds, context) {
    var lines = variantIds.map(function (id) {
      return { merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 };
    });

    return fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: CART_QUERY,
        variables: { lines: lines, attributes: context.attributes, country: context.country },
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (result) {
        var prices = {};
        var nodes = result && result.data && result.data.cartCreate && result.data.cartCreate.cart
          ? result.data.cartCreate.cart.lines.nodes
          : [];
        nodes.forEach(function (line) {
          var id = line.merchandise.id.split('/').pop();
          prices[id] = Math.round(parseFloat(line.cost.totalAmount.amount) * 100);
        });
        return prices;
      });
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function applyPrice(wrap, discounted) {
    var price = parseInt(wrap.dataset.price, 10);
    var compare = Math.max(parseInt(wrap.dataset.comparePrice, 10) || 0, price);
    if (!(discounted < price)) return;

    if (wrap.classList.contains('estee-pdp-price')) {
      applyThemePrice(wrap, discounted, compare);
      return;
    }

    var current = wrap.querySelector('.estee-price-current');
    var compareEl = wrap.querySelector('.estee-price-compare');
    if (!compareEl) {
      compareEl = document.createElement('span');
      compareEl.className = 'estee-price-compare';
      wrap.appendChild(compareEl);
    }
    current.textContent = formatMoney(discounted);
    compareEl.textContent = formatMoney(compare);

    setBadge(wrap.closest('.estee-product-card'), Math.round(((compare - discounted) * 100) / compare));
  }

  // "20% OFF" pill on the card image, same markup as snippets/icon-discount + the cart's .discount tag.
  // percent <= 0 removes it.
  var BADGE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-discount icon--extra-small" viewBox="0 0 15 15" fill="none">' +
    '<path d="M2.31547 7.56547C2.23358 7.48349 2.18755 7.37236 2.1875 7.25648V2.1875H7.25648C7.37236 2.18755 7.48349 2.23358 7.56547 2.31547L12.997 7.74703C13.079 7.82907 13.1251 7.94031 13.1251 8.05629C13.1251 8.17227 13.079 8.28351 12.997 8.36555L8.36719 12.997C8.28515 13.079 8.17391 13.1251 8.05793 13.1251C7.94195 13.1251 7.83071 13.079 7.74867 12.997L2.31547 7.56547Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M4.59375 5.25C4.95619 5.25 5.25 4.95619 5.25 4.59375C5.25 4.23131 4.95619 3.9375 4.59375 3.9375C4.23131 3.9375 3.9375 4.23131 3.9375 4.59375C3.9375 4.95619 4.23131 5.25 4.59375 5.25Z" fill="currentColor"/>' +
    '</svg>';

  function setBadge(card, percent) {
    var imageWrap = card && card.querySelector('.estee-product-image-wrap');
    if (!imageWrap) return;
    var badge = imageWrap.querySelector('.estee-badge-save');
    if (!(percent > 0)) {
      if (badge) badge.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'estee-badge-save discount text-sm-extra font-body-bold inline-flex items-center';
      badge.innerHTML = BADGE_ICON + '<span class="estee-badge-save__text"></span>';
      imageWrap.insertBefore(badge, imageWrap.firstChild);
    }
    badge.querySelector('.estee-badge-save__text').textContent = percent + '% OFF';
  }

  // Product page: switch the theme's own price block (snippets/price.liquid) to its on-sale
  // layout — discounted price, original crossed out — plus the same "20% OFF" pill.
  function applyThemePrice(wrap, discounted, compare) {
    var fPrice = wrap.querySelector('.f-price');
    var sale = fPrice && fPrice.querySelector('.f-price__sale');
    if (!sale) return;

    fPrice.classList.add('f-price--on-sale');
    fPrice.classList.remove('f-price--no-compare');

    var saleItem = sale.querySelector('.f-price-item--sale');
    if (saleItem) saleItem.textContent = formatMoney(discounted);

    var compareItem = sale.querySelector('.f-price-item--regular');
    if (!compareItem) {
      compareItem = document.createElement('span');
      compareItem.className = 'f-price-item f-price-item--regular';
      sale.appendChild(compareItem);
    }
    compareItem.innerHTML = '<s></s>';
    compareItem.firstChild.textContent = formatMoney(compare);

    var badge = sale.querySelector('.estee-badge-save');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'estee-badge-save discount text-sm-extra font-body-bold inline-flex items-center';
      badge.style.color = '#ffffff';
      badge.style.background = '#f5a623';
      badge.innerHTML = BADGE_ICON + '<span class="estee-badge-save__text"></span>';
      sale.appendChild(badge);
    }
    badge.querySelector('.estee-badge-save__text').textContent =
      Math.round(((compare - discounted) * 100) / compare) + '% OFF';
  }

  // Price display is cosmetic: any error here must never affect the page or the cart
  function update(root) {
    try {
      runUpdate(root);
    } catch (e) {}
  }

  function runUpdate(root) {
    var wraps = Array.prototype.slice.call(
      (root || document).querySelectorAll(
        '.estee-price-wrap[data-variant-id]:not([data-discount-checked]),' +
          ' .estee-pdp-price[data-variant-id]:not([data-discount-checked])'
      )
    );
    if (!wraps.length) return;
    wraps.forEach(function (wrap) {
      wrap.setAttribute('data-discount-checked', '');
    });

    var apply = function (prices) {
      wraps.forEach(function (wrap) {
        var discounted = prices[wrap.dataset.variantId];
        if (discounted !== undefined) applyPrice(wrap, discounted);
      });
    };

    getContext()
      .then(function (context) {
        var cache = readCache(context.key);
        apply(cache);

        var missing = wraps
          .map(function (wrap) {
            return wrap.dataset.variantId;
          })
          .filter(function (id, index, ids) {
            return ids.indexOf(id) === index && cache[id] === undefined;
          });
        if (!missing.length) return;

        return fetchPrices(missing, context).then(function (prices) {
          writeCache(context.key, Object.assign(readCache(context.key), prices));
          apply(prices);
        });
      })
      .catch(function () {
        // Cards keep their regular prices if the lookup fails.
      });
  }

  window.esteeDiscountPrices = { update: update, formatMoney: formatMoney, setBadge: setBadge };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      update();
    });
  } else {
    update();
  }
  // Sections re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (event) {
    update(event.target);
  });
  // Cards swapped in without a page load (collection filters, sorting, "load more")
  var pending = null;
  new MutationObserver(function () {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      update();
    }, 100);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
